/**
 * Shared dropdown option renderers for consistent styling across the Insights module.
 * Used by ScopeSelector, PulseDashboard, TrendingAgentsBoard, TopicIntelligencePanel.
 */

import React from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import { GlobalOutlined, RobotOutlined, TagsOutlined, OrderedListOutlined } from '@ant-design/icons';
import type { TenantNode } from '../types/insights';

// ─── Hierarchy colors (shared across all tenant selectors) ──────────────────
// Sourced from semantic tokens (--hierarchy-N) so the design system can recolor
// these consistently across light/dark themes and per-tenant brand overrides.

export const LEVEL_STYLES = [
  { bg: 'var(--hierarchy-0)' }, // 0 = root
  { bg: 'var(--hierarchy-1)' }, // 1 = org
  { bg: 'var(--hierarchy-2)' }, // 2 = dept
  { bg: 'var(--hierarchy-3)' }, // 3 = team
  { bg: 'var(--hierarchy-4)' }, // 4+ = sub
];

export const LEVEL_LABELS = ['ROOT', 'LEVEL 1', 'LEVEL 2', 'LEVEL 3', 'LEVEL 4', 'LEVEL 5'];

export function getLevelStyle(depth: number) {
  return LEVEL_STYLES[Math.min(depth, LEVEL_STYLES.length - 1)];
}

// ─── Shared icon badge ──────────────────────────────────────────────────────

const badgeStyle: React.CSSProperties = {
  width: 24, height: 24, borderRadius: 5,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  fontSize: 11, color: 'var(--color-grey-0)', fontWeight: 700, flexShrink: 0,
};

// ─── Agent option renderer ──────────────────────────────────────────────────

export function renderAgentOption(option: { value?: string | number; label?: React.ReactNode }) {
  const id = String(option.value ?? '');
  const name = String(option.label ?? '');
  // Strip trailing "(8charID)" from name if present, to show it separately in mono
  const cleanName = name.replace(/\s*\([a-f0-9]{8}\)\s*$/i, '');
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0' }}>
      <div style={{ ...badgeStyle, background: 'var(--primary)' }}>
        <RobotOutlined style={{ fontSize: 12 }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{ fontWeight: 500, fontSize: 13 }}>{cleanName}</span>
        {id && (
          <span style={{ fontSize: 10, color: 'var(--text-secondary)', fontFamily: 'monospace', marginInlineStart: 6 }} dir="auto">
            {id.slice(0, 8)}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Tenant option renderer ─────────────────────────────────────────────────

export function renderTenantOption(
  option: { value?: string | number; label?: React.ReactNode },
  tenantTree: TenantNode[],
  mutedTextColor = 'var(--text-secondary)',
) {
  const tenant = tenantTree.find(n => n.tenant_id === option.value);
  if (!tenant) {
    // Fallback: simple styled option (for tenants not in tree, e.g. from rankings)
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0' }}>
        <div style={{ ...badgeStyle, background: 'var(--text-muted)' }}>
          <GlobalOutlined style={{ fontSize: 12 }} />
        </div>
        <span style={{ fontWeight: 500, fontSize: 13 }} dir="auto">{String(option.label ?? option.value)}</span>
      </div>
    );
  }
  const ls = getLevelStyle(tenant.depth);
  const levelLabel = LEVEL_LABELS[Math.min(tenant.depth, LEVEL_LABELS.length - 1)];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0' }}>
      <div style={{ ...badgeStyle, background: ls.bg }}>
        {tenant.tenant_name.charAt(0).toUpperCase()}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontWeight: 500, fontSize: 13 }} dir="auto">{tenant.tenant_name}</span>
          <span style={{
            fontSize: 10, fontWeight: 600, color: ls.bg,
            background: `color-mix(in srgb, ${ls.bg} 9%, transparent)`,
            border: `1px solid color-mix(in srgb, ${ls.bg} 25%, transparent)`,
            borderRadius: 3, padding: '1px 5px', flexShrink: 0,
          }}>
            {levelLabel}
          </span>
        </div>
        {tenant.depth > 0 && (
          <div style={{ fontSize: 11, color: mutedTextColor, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} dir="auto">
            {tenant.hierarchy_path}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Template option renderer ────────────────────────────────────────────────

export function renderTemplateOption(option: { value?: string | number; label?: React.ReactNode }) {
  const isAll = option.value === 'all';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0' }}>
      <div style={{ ...badgeStyle, background: isAll ? 'var(--text-muted)' : 'var(--primary)' }}>
        {isAll ? <TagsOutlined style={{ fontSize: 12 }} /> : String(option.label ?? '').charAt(0).toUpperCase()}
      </div>
      <span style={{ fontWeight: 500, fontSize: 13 }} dir="auto">{option.label}</span>
    </div>
  );
}

// ─── Top N option renderer ───────────────────────────────────────────────────

export function renderTopNOption(option: { value?: string | number; label?: React.ReactNode }) {
  const isAll = option.value === 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0' }}>
      <div style={{ ...badgeStyle, background: isAll ? 'var(--text-muted)' : 'var(--primary)' }}>
        {isAll ? <OrderedListOutlined style={{ fontSize: 12 }} /> : `${option.value}`}
      </div>
      <span style={{ fontWeight: 500, fontSize: 13 }} dir="auto">{option.label}</span>
    </div>
  );
}

export function TopNPlaceholder({ text }: { text?: string }) {
  const { t } = useLanguage();
  text = text ?? t('common.topN', { count: 10 });
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12 }}>
      <OrderedListOutlined style={{ fontSize: 11 }} />
      {text}
    </span>
  );
}

// ─── Label renderers (for selected value in collapsed select) ───────────────

const selectedBadgeStyle: React.CSSProperties = {
  width: 18, height: 18, borderRadius: 4,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  fontSize: 9, color: 'var(--color-grey-0)', fontWeight: 700, flexShrink: 0,
};

export function renderTemplateLabel(props: { value?: string | number; label?: React.ReactNode }) {
  const isAll = props.value === 'all';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12 }}>
      <span style={{ ...selectedBadgeStyle, background: isAll ? 'var(--text-muted)' : 'var(--primary)' }}>
        {isAll ? <TagsOutlined style={{ fontSize: 9 }} /> : String(props.label ?? '').charAt(0).toUpperCase()}
      </span>
      {props.label}
    </span>
  );
}

export function renderTopNLabel(props: { value?: string | number; label?: React.ReactNode }) {
  const isAll = props.value === 0;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12 }}>
      <span style={{ ...selectedBadgeStyle, background: isAll ? 'var(--text-muted)' : 'var(--primary)' }}>
        {isAll ? <OrderedListOutlined style={{ fontSize: 9 }} /> : `${props.value}`}
      </span>
      {props.label}
    </span>
  );
}

// ─── Placeholder components ─────────────────────────────────────────────────

const placeholderStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 5, fontSize: 12,
};

export function AgentPlaceholder({ text }: { text?: string }) {
  const { t } = useLanguage();
  text = text ?? t('insights.selectors.allAgents');
  return (
    <span style={placeholderStyle}>
      <RobotOutlined style={{ fontSize: 11 }} />
      {text}
    </span>
  );
}

export function TenantPlaceholder({ text }: { text?: string }) {
  const { t } = useLanguage();
  text = text ?? t('insights.selectors.allTenants');
  return (
    <span style={placeholderStyle}>
      <GlobalOutlined style={{ fontSize: 11 }} />
      {text}
    </span>
  );
}

export function TemplatePlaceholder({ text }: { text?: string }) {
  const { t } = useLanguage();
  text = text ?? t('insights.selectors.allTopics');
  return (
    <span style={placeholderStyle}>
      <TagsOutlined style={{ fontSize: 11 }} />
      {text}
    </span>
  );
}

// ─── Shared select props ────────────────────────────────────────────────────

export const AGENT_SELECT_STYLE: React.CSSProperties = { minWidth: 200, maxWidth: 340 };
export const TENANT_SELECT_STYLE: React.CSSProperties = { minWidth: 200, maxWidth: 340 };
export const AGENT_DROPDOWN_STYLE: React.CSSProperties = { minWidth: 300 };
export const TENANT_DROPDOWN_STYLE: React.CSSProperties = { minWidth: 320 };

// Agent filterOption: matches by name or ID
export function agentFilterOption(input: string, option: any): boolean {
  const q = input.toLowerCase();
  const label = String(option?.label ?? '').toLowerCase();
  const value = String(option?.value ?? '').toLowerCase();
  return label.includes(q) || value.startsWith(q);
}

// Build tenant options from tree with searchText
export function buildTenantOptions(tenantTree: TenantNode[]) {
  return tenantTree.map(n => ({
    value: n.tenant_id,
    label: n.tenant_name,
    searchText: `${n.tenant_name} ${n.hierarchy_path} ${n.tenant_id.slice(0, 8)}`.toLowerCase(),
  }));
}

// Tenant filterOption for searchText
export function tenantFilterOption(input: string, option: any): boolean {
  const searchText = option?.searchText || '';
  return searchText.includes(input.toLowerCase());
}
